/**
 * What the kitchen still has to make, in two modes.
 *
 * This was a panel down the side of the orders board, then a flat list of dishes. Both
 * were one answer to two different questions, and the kitchen asks both:
 *
 * - **By order** takes the order as the unit, because the order is what is handed over:
 *   everything on one ticket is plated together, checked together and called together.
 * - **By batch** takes the dish as the unit, because a wok, a steamer and a fryer hold
 *   several at once. A round is a window of food that can be cooked and handed over
 *   together, which is what makes a batch worth starting.
 *
 * They read one clock — the ready-by — and being late stays the board's own clock, so
 * the two screens cannot disagree about which order is behind. Only orders the counter
 * has accepted count: an unaccepted order can still be refused, and a batch total is a
 * number somebody starts a wok on.
 *
 * By batch the page is also a control: a round can be ticked off, and ticking it marks
 * every order in it ready through `merchant_set_order_status`. That is deliberately the
 * same transition the board's own button makes, written once per ticket — the food is
 * made, the customer is told to collect — rather than a second "cooked" state that could
 * disagree with the first. A round is a view of orders, so it is finished by finishing
 * them, and it leaves this page as they do.
 *
 * By order the ticket carries the same action for one order, because the ticket is what
 * a cook plates and calls: making it and then walking to the board to say so is the one
 * step of the job that is not theirs. It asks nothing — the board does not ask for a
 * single order either, and the round's dialog exists only because one press there
 * reaches several customers at once.
 *
 * One order in a round can have been accepted after the round opened — a booked order
 * keeps the slot it was booked for — and the tick would mark that one ready without it
 * necessarily being cooked. The page flags it, and the confirmation says it in a
 * sentence, because the cook is the only person who can tell.
 */

import { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { CheckCheck, RefreshCw } from 'lucide-react-native';
import { completeCookRound, fetchCookQueue } from '@yummy/db';
import {
  boardBand,
  boardSort,
  cookBatchCount,
  cookRoundSeconds,
  cookRounds,
  formatClock,
  formatDuration,
  isCooking,
  roundCutoff,
  lateAt,
  type BoardBand,
  type CookRound,
  type CookRoundBatch,
  type MessageKey,
  type Order,
} from '@yummy/shared';
import {
  Button,
  Callout,
  Card,
  CardGrid,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  HeadingStat,
  IconControl,
  LoadingState,
  PageHeading,
  Row,
  Screen,
  SegmentedControl,
  Stack,
  TONE_TEXT,
  Text,
  TitledCard,
  ToneBadge,
  cn,
  useAsync,
  useNow,
  usePersistentState,
  useResponsive,
  useT,
  useTheme,
  useToast,
} from '@yummy/ui-rnr';

import { useOrders } from '../app/orders';
import { useMerchant } from '../app/auth';
import { supabase } from '../lib/supabase';

/** The clock the bands and the countdowns are quantised to. */
const BAND_TICK_MS = 10_000;

/**
 * How often the round-cutoff policy is re-read.
 *
 * Its arrival rate is a trailing hour, so a value fetched once would describe
 * whichever hour the page was opened in. Five minutes is well inside the hour and
 * far outside the tick that redraws the bands, so the two clocks stay separate.
 */
const POLICY_TICK_MS = 5 * 60_000;

/**
 * Where the load line joins the advisory.
 *
 * The same utilisation the Capacity page grades as "nearly saturated", so the two
 * screens call the same number crowded.
 */
const CROWDED_LOAD = 0.85;

/**
 * Past this the batch row puts the ticket numbers under the dish instead of beside it.
 *
 * The same breakpoint the old panel used: in a narrow column the two facts compete for
 * one line, and the quantity and the dish are the two that must not lose it.
 */
const SIDE_CAPTION = 900;

/**
 * The chrome beside the board, and the narrowest a ticket may be.
 *
 * `CardGrid` works its column count out from the window, which knows nothing about the
 * rail in front of it: at a 1080 window the tickets were handed thirds of the window
 * while the rail ate 236 of it, and at 1600 the page asked for five columns of 263 — a
 * 247-point ticket, smaller than the iPad's, and never the 320 the grid had been asked
 * for. The board keeps the same 300 of chrome and subtracts it before dividing; this
 * does too, against the 380 a ticket was always meant to have. Two to a row on an iPad,
 * three on a desktop, one on a phone.
 */
const CHROME_WIDTH = 300;
const TICKET_MIN_WIDTH = 380;

/**
 * Where the chosen mode is kept between visits.
 *
 * Per device and per browser, the same answer as the rail's collapsed state: this is a
 * property of the pass the tablet sits on, not of the shop or of the person logged in. The
 * key carries the merchant's prefix so the two apps cannot collide if they ever share one.
 */
const COOK_MODE_KEY = 'yummy.merchant.cook.mode';

/** The two ways the kitchen can read one queue. */
type CookMode = 'order' | 'batch';

/** The board's bands, worst first, which is the order a cook works them in. */
const BAND_ORDER: readonly BoardBand[] = ['late', 'now', 'next', 'later'];

const BAND_LABEL: Record<BoardBand, MessageKey> = {
  late: 'merchant.orders.bandLate',
  now: 'merchant.orders.bandNow',
  next: 'merchant.orders.bandNext',
  later: 'merchant.cook.bandLater',
};

const BAND_RULE: Record<BoardBand, MessageKey> = {
  late: 'merchant.cook.bandLateRule',
  now: 'merchant.cook.bandNowRule',
  next: 'merchant.orders.bandNextRule',
  later: 'merchant.orders.bandNextRule',
};

const BAND_TONE: Record<BoardBand, string> = {
  late: 'text-destructive',
  now: 'text-warning',
  next: 'text-muted-foreground',
  later: 'text-muted-foreground',
};

/**
 * A ticket's leading edge, by the band it sits in.
 *
 * The card takes the dashboard's shape — a band naming it, a panel holding the
 * work — and the colour stays where this board has always put it: three points on
 * the card's edge, with the pressure in the same tone. The band is the grey every
 * other card's band is, so the tone has one carrier rather than two.
 */
const TICKET_ACCENT: Record<BoardBand, string> = {
  late: 'border-l-destructive border-l-[3px]',
  now: 'border-l-warning border-l-[3px]',
  next: 'border-l-subtle-foreground border-l-[3px]',
  later: 'border-l-subtle-foreground border-l-[3px]',
};

/** The pressure line's ink, by the band it sits in. */
const TICKET_HEADLINE: Record<BoardBand, string> = {
  late: TONE_TEXT.danger,
  now: TONE_TEXT.warning,
  // A clock time is not a warning: the bands that are merely due keep the
  // foreground ink they have always had.
  next: '',
  later: '',
};

/**
 * The option combination a line was ordered in, as one thing to make.
 *
 * @param line The order line.
 * @returns The label, or an empty string for a dish sold whole.
 */
function optionLabel(line: Order['items'][number]): string {
  return [line.variant_name_snapshot, ...(line.options ?? []).map((option) => option.name)]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
}

/**
 * One order, as the kitchen makes it.
 *
 * @param props The order, the band it is in, the quantised clock, and the ticket's
 *   own action state.
 * @returns The ticket.
 */
function Ticket({
  order,
  band,
  now,
  onMarkReady,
  marking,
  disabled,
}: {
  order: Order;
  band: BoardBand;
  now: number;
  onMarkReady: (order: Order) => void;
  /** True while this order is the one being written. */
  marking: boolean;
  /** True while any order is being written, in either tab. */
  disabled: boolean;
}) {
  const t = useT();
  const theme = useTheme();
  const readyClock = formatClock(order.eta_ready_at, order.timezone);
  const minutes = Math.round((Date.parse(order.eta_ready_at) - now) / 60_000);
  const wentLate = lateAt(order);
  const lateMinutes =
    wentLate === null ? 1 : Math.max(1, Math.round((now - wentLate) / 60_000));

  // What leads is the pressure, not the clock: a cook reading across the pass wants to
  // know which ticket is in trouble, and the deadline is the second thing.
  const headline =
    band === 'late'
      ? t('merchant.cook.lateBy', { count: lateMinutes })
      : band === 'now'
        ? minutes <= 1
          ? t('merchant.cook.readyNow')
          : t('merchant.cook.inMinutes', { count: minutes })
        : readyClock;
  const caption =
    band === 'late' || band === 'now'
      ? t('merchant.cook.readyBy', { time: readyClock })
      : t('merchant.cook.readyByLabel');
  const startBy = order.prep_start_at
    ? formatClock(order.prep_start_at, order.timezone)
    : null;

  return (
    <TitledCard
      className={TICKET_ACCENT[band]}
      title={`#${order.order_number}`}
      // The number is the ticket's identity and the thing a kitchen calls across
      // the pass, so it keeps the size and weight it has always had. `leading-none`
      // is what keeps the band the height of every other card's: 24 points of line
      // box is exactly what the dashboard's 16/600 band title occupies.
      titleClassName="text-2xl leading-none font-extrabold"
      // The table is the one thing on this line a cook acts on, so it is set as a
      // label rather than a whisper — 12/600 in the ink that clears AA on the band
      // (4.63:1, where the default subtle grey is 2.46). Whose order it is belongs to
      // the counter: the name was never read here, and on a walk-up it was the only
      // thing on the line.
      description={order.table_label ?? undefined}
      descriptionClassName="text-muted-foreground font-semibold"
      // A ticket is read at a glance from a metre away, so its band keeps less air
      // than a card of figures does: 8 rather than 12.
      bandClassName="py-2"
      // The two columns are given the same rhythm — a 24-point first line, a 2-point
      // gap, a 16-point second — so the clock's caption sits on the table's centre
      // line rather than two points above it. `leading-6` is what the number's own
      // `leading-none` at 24 points occupies; without it the pressure's 20-point line
      // box pulls everything above it up by two.
      action={
        <Stack gap="xs" align="end" className="shrink-0">
          <Text className={cn('text-sm leading-6 font-bold', TICKET_HEADLINE[band])}>
            {headline}
          </Text>
          <Text className="-mt-0.5 text-subtle-foreground text-xs">{caption}</Text>
        </Stack>
      }
      // A booked ticket carries the one fact the clock does not: the moment the
      // kitchen has to start. Accepting is not starting, so this turns a future
      // slot into a job for now — said in one sentence rather than two chips, right
      // aligned against the clock above it.
      bandFooter={
        startBy ? (
          // Blue, and the only blue on the board: `--info` is what the Scheduled chip
          // wore before the chips became a sentence, so the colour already means
          // "booked ahead" here rather than being a new hue. It is also the tone with
          // the most room on the band — 6.38:1 against AA's 4.5 — which a line
          // carrying a deadline wants.
          <Text className="text-info text-right text-xs">
            {t('merchant.cook.scheduledStartBy', { time: startBy })}
          </Text>
        ) : undefined
      }
      // The panel's own 16 points of padding is a dashboard card's air; a ticket's
      // rows sit closer together than that, and `gap-1` is already the 4 points
      // that separates one dish from the next.
      panelClassName="px-3.5 py-3"
      // The ticket's one action, under the work it finishes: the same footing, the
      // same green and the same words-stay-while-busy as the round card's, so one
      // page teaches one gesture. It asks nothing — a ticket is one order, and the
      // board does not ask for one order either. The accessible name adds the number,
      // because a grid of these is a grid of identical buttons to a screen reader;
      // it leads with the visible label, which is what label-in-name wants. No rule
      // above it: the panel's own padding already sets the button apart, and a line
      // there drew a second edge across a card that only wanted one.
      panelFooter={
        <Stack className="px-3.5 py-3">
          <Button
            variant="success"
            className="w-full"
            onPress={() => onMarkReady(order)}
            disabled={disabled}
            aria-busy={marking}
            accessibilityLabel={t('merchant.cook.markReadyAria', {
              number: `#${order.order_number}`,
            })}>
            {marking ? (
              <ActivityIndicator size="small" color={theme.palette.primaryForeground} />
            ) : null}
            <Text>{t('merchant.cook.markReady')}</Text>
          </Button>
        </Stack>
      }>
      {order.items.map((line) => {
        const option = optionLabel(line);
        return (
          <Stack key={line.id} gap="xs">
            <Text className="text-base">
              {/* The size is repeated rather than inherited: react-native-web inherits
                  the parent's colour but not its font size. */}
              <Text className="text-base font-extrabold">{`${line.quantity}×`}</Text>{' '}
              {line.name}
            </Text>
            {option ? (
              // The choices are what the dish is made of, not a footnote: at 12
              // points and indented they already sit under the name, so the ink is
              // the name's own rather than a grey that has to be leaned into.
              <Text className="pl-6 text-xs">{option}</Text>
            ) : null}
          </Stack>
        );
      })}
    </TitledCard>
  );
}
/**
 * One dish inside a round, with the tickets it clears.
 *
 * @param props The batch and whether the row has to stack.
 * @returns The row.
 */
function BatchRow({
  batch,
  stacked,
  first,
  byId,
}: {
  batch: CookRoundBatch;
  stacked: boolean;
  first: boolean;
  byId: Map<string, Order>;
}) {
  const t = useT();
  const tickets = (
    <Row
      gap="xs"
      align="center"
      wrap
      className={stacked ? undefined : 'max-w-[190px] shrink-0 justify-end'}>
      {batch.orderIds.map((id) => {
        const order = byId.get(id);
        // Three states, and the colour is the only thing that can carry the third: a
        // booked order is the one in this batch holding a slot a customer chose, so it
        // wears the tone the board already gives a scheduled order. A walk-up is
        // ordinary, and one that joined after the round opened stays a warning.
        const tone = batch.lateAcceptedOrderIds.includes(id)
          ? 'warning'
          : order?.is_scheduled
            ? 'info'
            : 'neutral';
        return (
          <ToneBadge key={id} label={`#${order?.order_number ?? '?'}`} tone={tone} size="sm" />
        );
      })}
    </Row>
  );

  return (
    <Row gap="md" align="start" className={cn('border-border px-3.5 py-3', !first && 'border-t')}>
      <Text className="text-primary-strong min-w-[54px] text-[26px] leading-7 font-extrabold">
        {batch.quantity}
      </Text>
      <Stack gap="xs" className="min-w-0 flex-1">
        <Text className="text-lg font-semibold">{batch.name}</Text>
        {/* A narrow row puts the tickets here, directly under the name, rather than
            trailing off the bottom of the group: there they read as though they belong
            to the dish in the row below, which is the one thing a chip must never do. */}
        {stacked ? tickets : null}
        {/* No caption for a dish sold whole. The ticket chips on the right already name
            every order this batch clears, so "from 2 orders" was the same fact said
            twice, and a number the cook can read off the chips does not earn a line.
            A dish with options keeps its breakdown, because that is what the chips
            cannot say: two plain and one with chilli, not three of one thing. */}
        {batch.options.map((option) => (
          <Row key={option.name} gap="sm" align="baseline">
            {/* The count and the option are one line, the way an item reads on a
                ticket: `1× 不辣` says what to make, where a count in its own column
                says how many of something the eye has to travel to find. */}
            <Text className="text-base">
              {/* The size is repeated rather than inherited: react-native-web passes
                  the parent's colour down but not its font size. */}
              <Text className="text-base font-extrabold">{`${option.quantity}×`}</Text>{' '}
              {option.name}
            </Text>
            {option.lateAcceptedOrderIds.length > 0 ? (
              <Text className="text-warning text-[11px] font-bold">
                {t('merchant.cook.lateAccepted', { count: option.lateAcceptedOrderIds.length })}
              </Text>
            ) : null}
          </Row>
        ))}
      </Stack>
      {stacked ? null : tickets}
    </Row>
  );
}

/**
 * A round as it was drawn, frozen when its button was pressed.
 *
 * The dialog and the writes read the same list. A round's membership is live — a
 * booked order the counter takes late joins a window that is already open — so a
 * dialog built from a fresh read could name one set of orders and mark another. The
 * snapshot is what makes the sentence and the tap the same act.
 */
interface RoundTick {
  /** Identifies the round while the writes run, so its own button reports. */
  readyBy: number;
  timezone: string;
  orderIds: string[];
  lateAcceptedOrderIds: string[];
}

/**
 * A round of work: everything wanted within one window of its first ready-by.
 *
 * @param props The round, the restaurant's timezone, the clock, whether to stack, and
 *   the tick's own state.
 * @returns The round's card.
 */
function RoundCard({
  round,
  timezone,
  now,
  stacked,
  byId,
  onMarkReady,
  marking,
  disabled,
}: {
  round: CookRound;
  timezone: string;
  now: number;
  stacked: boolean;
  byId: Map<string, Order>;
  onMarkReady: (round: CookRound) => void;
  /** True while this round's orders are the ones being written. */
  marking: boolean;
  /** True while any round is being written, or a confirmation is open. */
  disabled: boolean;
}) {
  const t = useT();
  const theme = useTheme();
  const readyClock = formatClock(new Date(round.readyBy).toISOString(), timezone);
  const lateMinutes =
    round.lateBy === null ? 0 : Math.max(1, Math.round((now - round.lateBy) / 60_000));
  const lateAccepted = round.lateAcceptedOrderIds.length;

  return (
    <Card className={cn('gap-0 overflow-hidden p-0', round.late && 'border-l-destructive border-l-[3px]')}>
      <Row
        spread
        gap="md"
        align="center"
        wrap
        className="border-border bg-muted gap-0 border-b px-3.5 py-2.5">
        <Row gap="sm" align="center" wrap>
          <Text className={cn('text-sm font-bold', round.late && 'text-destructive')}>
            {readyClock === '' || round.readyBy > now
              ? t('merchant.cook.roundReadyAt', { time: readyClock })
              : t('merchant.cook.roundReadyNow', { time: readyClock })}
          </Text>
          {/* Twelve points of label in two points of padding, which makes the pill
              exactly the height of the clock's own line — so the chip is centred on
              the line rather than hanging below it. The label then rides a point
              above that line's baseline, which is as close as a smaller label gets
              to both: on the baseline instead, the pill's padding has to go
              three-and-one and the pill reads lopsided, which is worse. */}
          {round.late ? (
            <ToneBadge
              label={t('merchant.cook.lateBy', { count: lateMinutes })}
              tone="danger"
              size="md"
              className="px-2 py-0.5"
            />
          ) : null}
        </Row>
        <Row gap="sm" align="center" wrap>
          <Text className="text-subtle-foreground text-xs">
            {t('common.itemCount', { count: round.itemCount })}
            {' · '}
            {t('merchant.cook.orderSummary', { count: round.orderIds.length })}
          </Text>
          {lateAccepted > 0 ? (
            <Text className="text-warning text-xs font-bold">
              {t('merchant.cook.roundLateAccepted', { count: lateAccepted })}
            </Text>
          ) : null}
        </Row>
      </Row>

      {round.batches.map((batch, index) => (
        <BatchRow
          key={`${batch.category}\u0000${batch.name}`}
          batch={batch}
          stacked={stacked}
          first={index === 0}
          byId={byId}
        />
      ))}

      {/* The round's one action, under the work it finishes. Full width and green,
          which is what the board puts on the single transition that completes the
          food; the label carries the count, because one tap reaches every ticket in
          the round and that is the number worth reading before it is pressed. The
          accessible name leads with those same words and adds the round's clock:
          several of these sit on one page and the time is what tells them apart. */}
      <Stack className="border-border border-t px-3.5 py-3">
        <Button
          variant="success"
          className="w-full"
          onPress={() => onMarkReady(round)}
          disabled={disabled}
          aria-busy={marking}
          accessibilityLabel={
            readyClock
              ? t('merchant.cook.markRoundReadyAria', {
                  count: round.orderIds.length,
                  time: readyClock,
                })
              : t('merchant.cook.markRoundReady', { count: round.orderIds.length })
          }>
          {/* The words stay while it works. The button is full width so it cannot
              collapse, but an empty green bar with a lone spinner in it reads as a
              broken control rather than a busy one — and the label is the only thing
              on the page saying which round is being written. The spinner is the
              app's own `ActivityIndicator` in the button's foreground colour, the
              same busy state the board's hand-over button shows. */}
          {marking ? (
            <ActivityIndicator size="small" color={theme.palette.primaryForeground} />
          ) : null}
          <Text>{t('merchant.cook.markRoundReady', { count: round.orderIds.length })}</Text>
        </Button>
      </Stack>
    </Card>
  );
}

/**
 * The kitchen's list of what is still outstanding.
 *
 * @returns The cook screen.
 */
export function CookScreen() {
  const t = useT();
  const { queue, loading, initial, error, reload, applyOrder, connection } = useOrders();
  const { restaurant } = useMerchant();
  const { width } = useResponsive();
  // Batch, because that is the page's own job and the number the rail shows. By order
  // is the counter's question — the board answers it — and opening on it made the kitchen
  // screen start on a view of somebody else's work. The default, not a decision: a kitchen
  // that works by order says so once and is believed on the next visit too. Read
  // synchronously, so the first paint is already the chosen mode rather than batch
  // flickering into order under the cook's hands.
  const [storedMode, setMode] = usePersistentState<CookMode>(COOK_MODE_KEY, 'batch');
  // Anything else in storage — a value written by another version, an edited one — reads as
  // the default rather than as a third mode the control cannot show.
  const mode: CookMode = storedMode === 'order' ? 'order' : 'batch';

  // Quantised: the bands and the countdowns land on the same tick, so two screens
  // agreeing about "late" agree between ticks as well.
  const now = Math.floor(useNow().getTime() / BAND_TICK_MS) * BAND_TICK_MS;
  const stacked = width < SIDE_CAPTION;
  /** How many tickets to a row — see `TICKET_MIN_WIDTH`. */
  const ticketColumns = Math.max(1, Math.floor((width - CHROME_WIDTH) / TICKET_MIN_WIDTH));

  const work = useMemo(() => queue.filter(isCooking), [queue]);
  // The shop's own round length, or the default until its settings arrive. Read from the
  // restaurant rather than counted here, so the rail's badge beside this page — which
  // reads the same field — cannot be counting a different window.
  const roundSeconds = cookRoundSeconds(restaurant);
  const rounds = useMemo(() => cookRounds(queue, now, roundSeconds), [queue, now, roundSeconds]);
  const items = useMemo(
    () => rounds.reduce((sum, round) => sum + round.itemCount, 0),
    [rounds]
  );
  /**
   * What the batch mode holds: the batches to cook, not the dishes they belong to.
   *
   * Counted by the shared function rather than here, because the rail's badge shows the
   * same number — a badge that disagreed with the page it points at is worse than none.
   */
  const batches = useMemo(() => cookBatchCount(queue, now, roundSeconds), [queue, now, roundSeconds]);

  // ---------------------------------------------------------------------------
  // When to cut the next round
  // ---------------------------------------------------------------------------
  // A live advisory, not automation. The fixed window still draws the rounds above;
  // this says when the window is doing the kitchen a disservice — a round that has
  // already filled, or an order that has waited as long as the window would ever
  // have made it wait. See `docs/cook-rounds.md`.
  //
  // The policy is refetched every five minutes because its arrival rate is a
  // trailing hour: reading it once on mount would leave "the last hour" meaning
  // whatever hour the page happened to be opened in. The bucket is derived from the
  // page's own clock, so it costs no timer of its own.
  const policyBucket = Math.floor(now / POLICY_TICK_MS);
  const policy = useAsync(
    async () => (restaurant?.id ? fetchCookQueue(supabase, restaurant.id) : null),
    [restaurant?.id, policyBucket],
  );

  /**
   * Who is waiting to be cooked: the walk-ups.
   *
   * A booked order is not waiting — it has a slot, and one placed this morning would
   * otherwise read as hours of waiting and drag the whole advisory with it. The same
   * exclusion the estimator makes, for the same reason.
   */
  const waiting = useMemo(() => work.filter((order) => order.scheduled_for === null), [work]);

  const oldestWaitSeconds = useMemo(() => {
    let oldest: number | null = null;
    for (const order of waiting) {
      const placed = Date.parse(order.placed_at);
      if (!Number.isFinite(placed)) continue;
      if (oldest === null || placed < oldest) oldest = placed;
    }
    return oldest === null ? 0 : Math.max(0, (now - oldest) / 1000);
  }, [waiting, now]);

  // Decided here rather than by the server, because the wait that decides it grows
  // with the clock: an answer fetched once would be stale the moment an order
  // crossed its ceiling. `roundCutoff` is pure, so this is a re-render and not a
  // request.
  const cutoff = useMemo(() => {
    if (!policy.data) return null;
    return roundCutoff({
      waiting: waiting.length,
      oldestWaitSeconds,
      targetBatch: policy.data.target_batch,
      maxWaitSeconds: policy.data.max_wait_seconds,
    });
  }, [policy.data, waiting.length, oldestWaitSeconds]);

  /**
   * The tickets, grouped by the board's bands and ordered the board's own way.
   *
   * Earliest deadline first — `boardSort`, the same comparator the counter's screen uses
   * — rather than the earliest ready-by. Inside Late the two disagree, and visibly: a
   * booked order goes late at its start-by plus the merchant's grace, which can be long
   * before the slot its ready-by names, so sorting by ready-by left the worst-late ticket
   * sitting fourth behind three that were less late than it. The number a cook reads on
   * the card is minutes late, and the row has to agree with it.
   */
  const bands = useMemo(() => {
    const groups: Record<BoardBand, Order[]> = { late: [], now: [], next: [], later: [] };
    for (const order of work) groups[boardBand(order, now)].push(order);
    for (const band of BAND_ORDER) groups[band].sort(boardSort);
    return groups;
  }, [work, now]);

  /** The queue by id: a round carries ids, and the card shows numbers and clock times. */
  const byId = useMemo(() => new Map(queue.map((order) => [order.id, order])), [queue]);

  const lateCount = useMemo(
    () => work.filter((order) => boardBand(order, now) === 'late').length,
    [work, now]
  );

  /** The restaurant's own clock, which every instant on this page is read in. */
  const timezone = work[0]?.timezone ?? 'UTC';

  // ---------------------------------------------------------------------------
  // Ticking a round off
  // ---------------------------------------------------------------------------
  // A round is a view of orders, so finishing it is the order's own transition —
  // but written as one batch, not once per ticket. `orders.status` stays the only
  // place order state lives, the round leaves this page as its orders leave the
  // queue, and the board shows them ready for collection. The same write is where
  // the round becomes a record: the server stores its membership, its deadline and
  // the inferred moment it started, which is what makes batch-wait measurable
  // later. See `docs/cook-rounds.md`.
  const toast = useToast();
  /** The round a confirmation is open for, frozen as it was drawn. */
  const [pending, setPending] = useState<RoundTick | null>(null);
  /** The ready-by of the round being written, so its own button reports. */
  const [marking, setMarking] = useState<number | null>(null);
  /** The order whose ticket is being written, by the same rule. */
  const [markingOrderId, setMarkingOrderId] = useState<string | null>(null);

  /**
   * Marks every order in one frozen round ready, in a single write.
   *
   * All or nothing, which is the whole point rather than a detail. The page has no
   * concept of a partially marked round, and a half-applied batch would tell some
   * customers to collect while the rest of the wok was still being plated. So an
   * order somebody already dealt with refuses the entire call and nothing moves —
   * which also means there is no half-finished batch to report, and the round is
   * still on the page with its own button when the write fails.
   */
  const runTick = useCallback(
    async (tick: RoundTick) => {
      setMarking(tick.readyBy);
      try {
        const updated = await completeCookRound(supabase, tick.orderIds, tick.readyBy, {
          source: 'round',
          lateAcceptedIds: tick.lateAcceptedOrderIds,
        });
        for (const order of updated) applyOrder(order);
        toast.show(t('merchant.cook.roundReadyToast', { count: updated.length }), {
          tone: 'success',
        });
      } catch (cause) {
        toast.show(
          t('merchant.cook.markRoundFailed', {
            reason: cause instanceof Error ? cause.message : String(cause),
          }),
          { tone: 'danger' },
        );
        // A refused write means this screen's copy of the queue may be stale.
        void reload();
      } finally {
        setMarking(null);
        setPending(null);
      }
    },
    [applyOrder, reload, toast, t]
  );

  /**
   * Marks one ticket's order ready.
   *
   * The by-order view's own action, and a batch of one as far as the record is
   * concerned: `source: 'single'` says the cook plucked this bag out rather than
   * bringing a whole round off, so the row is kept — capacity was freed, and the
   * log should say what left the kitchen — but it is never read as evidence about
   * batching. See `docs/cook-rounds.md`.
   */
  const markOrderReady = useCallback(
    async (order: Order) => {
      if (markingOrderId !== null || marking !== null || pending !== null) return;
      setMarkingOrderId(order.id);
      try {
        const updated = await completeCookRound(supabase, [order.id], Date.parse(order.eta_ready_at), {
          source: 'single',
        });
        for (const next of updated) applyOrder(next);
        toast.show(t('merchant.cook.roundReadyToast', { count: 1 }), { tone: 'success' });
      } catch (cause) {
        toast.show(
          t('merchant.cook.markFailedOne', {
            number: `#${order.order_number}`,
            reason: cause instanceof Error ? cause.message : String(cause),
          }),
          { tone: 'danger' },
        );
        // A refused write means this screen's copy may be stale.
        void reload();
      } finally {
        setMarkingOrderId(null);
      }
    },
    [applyOrder, marking, markingOrderId, pending, reload, toast, t]
  );

  /**
   * The round's button: confirm first when the confirmation has something to add.
   *
   * The dialog exists for the two facts a tap does not already state — how many
   * customers one press reaches, and an order the counter accepted after this round had
   * opened, which may not be cooked at all. A round of one order with no late joiner is
   * the board's own single-order action, so it goes straight through rather than adding
   * a dialog the board would not.
   */
  const startTick = useCallback(
    (round: CookRound) => {
      if (marking !== null || pending !== null) return;
      const tick: RoundTick = {
        readyBy: round.readyBy,
        timezone,
        orderIds: [...round.orderIds],
        lateAcceptedOrderIds: [...round.lateAcceptedOrderIds],
      };
      if (tick.orderIds.length > 1 || tick.lateAcceptedOrderIds.length > 0) {
        setPending(tick);
        return;
      }
      void runTick(tick);
    },
    [marking, pending, runTick, timezone]
  );

  /**
   * The late joiner, for the dialog. Counted rather than listed: the number is the fact
   * that matters, and the ticket numbers are already on the card behind it.
   */
  const lateNote = useMemo(() => {
    if (!pending || pending.lateAcceptedOrderIds.length === 0) return null;
    const lateId = pending.lateAcceptedOrderIds[0];
    const first = lateId ? byId.get(lateId) : undefined;
    return t('merchant.cook.markRoundReadyLateNote', {
      count: pending.lateAcceptedOrderIds.length,
      time: first?.accepted_at ? formatClock(first.accepted_at, pending.timezone) : '',
    });
  }, [pending, byId, t]);

  if (initial) {
    return (
      <Screen>
        <LoadingState message={t('merchant.orders.loading')} />
      </Screen>
    );
  }

  const refreshLabel = loading ? t('common.refreshing') : t('common.refresh');

  return (
    <Screen constrain={false}>
      <Stack gap="lg" className="flex-1">
        <PageHeading
          title={t('merchant.cook.title')}
          minWidth={240}
          subtitle={
            <Row gap="sm" align="center" wrap>
              {/* Batch mode says how many batches, because that is what its badge counts
                  and a bare number beside a mode is not something a merchant can check
                  against the screen. By order has no equivalent: its badge is the orders,
                  which the line already names. */}
              {mode === 'batch' ? (
                <HeadingStat>{t('merchant.cook.batchSummary', { count: batches })}</HeadingStat>
              ) : null}
              <HeadingStat>{t('common.itemCount', { count: items })}</HeadingStat>
              <HeadingStat>{t('merchant.cook.orderSummary', { count: work.length })}</HeadingStat>
              {mode === 'batch' ? (
                <HeadingStat>
                  {t('merchant.cook.roundSummary', { count: rounds.length })}
                </HeadingStat>
              ) : null}
              {/* The counts with something to say: an absent count is the signal, so a
                  calm board reads as one line rather than two. */}
              {lateCount > 0 ? (
                <HeadingStat tone="destructive">
                  {t('merchant.orders.lateCount', { count: lateCount })}
                </HeadingStat>
              ) : null}
              {connection === 'offline' ? (
                <ToneBadge label={t('merchant.connection.offline')} tone="warning" size="sm" />
              ) : null}
            </Row>
          }
          action={
            <Row gap="sm" align="center" wrap>
              {/* The mode is a lens on one queue, so it sits with the title
                  rather than in settings. Both counts stay visible: the other
                  mode's size is a fact about this queue, not a reason to go and
                  look. */}
              <SegmentedControl<CookMode>
                options={[
                  { value: 'order', label: t('merchant.cook.byOrder'), badge: work.length },
                  { value: 'batch', label: t('merchant.cook.byBatch'), badge: batches },
                ]}
                value={mode}
                onChange={setMode}
              />
              {/* The list keeps itself current over realtime and falls back to a poll, so
                  this is for the moment a cook does not trust the screen rather than the way
                  it normally updates. The same icon control the board uses, because it is the
                  same action — a labelled button here and a glyph there read as two. */}
              <IconControl
                label={refreshLabel}
                icon={RefreshCw}
                onPress={() => void reload()}
                disabled={loading}
              />
            </Row>
          }
        />

        {error ? <ErrorBanner message={error} onRetry={() => void reload()} /> : null}

        {work.length === 0 ? (
          <EmptyState
            icon={CheckCheck}
            title={t('merchant.cook.emptyTitle')}
            message={t('merchant.cook.emptyHint')}
          />
        ) : mode === 'order' ? (
          <Stack gap="lg">
            {BAND_ORDER.map((band) =>
              bands[band].length === 0 ? null : (
                <Stack key={band} gap="sm">
                  <Row gap="sm" align="baseline" wrap>
                    <Text
                      className={cn(
                        'text-xs font-extrabold tracking-widest uppercase',
                        BAND_TONE[band]
                      )}>
                      {t(BAND_LABEL[band])}
                    </Text>
                    <Text className="text-subtle-foreground text-xs">{t(BAND_RULE[band])}</Text>
                  </Row>
                  <CardGrid columns={ticketColumns}>
                    {bands[band].map((order) => (
                      <Ticket
                        key={order.id}
                        order={order}
                        band={band}
                        now={now}
                        onMarkReady={markOrderReady}
                        marking={markingOrderId === order.id}
                        disabled={markingOrderId !== null || marking !== null || pending !== null}
                      />
                    ))}
                  </CardGrid>
                </Stack>
              )
            )}
          </Stack>
        ) : (
          <Stack gap="md">
            {/* The cutoff, when there is one to make. Batch mode only: the sentence is
                about cutting a round, and by order the cook is plating one ticket at a
                time. Nothing is shown when nothing is waiting — an advisory that appears
                on a calm board is noise. */}
            {cutoff && cutoff.reason !== 'nothing' ? (
              <Stack gap="xs">
                <Callout
                  tone={
                    cutoff.reason === 'enough'
                      ? 'success'
                      : cutoff.reason === 'waited'
                        ? 'warning'
                        : 'info'
                  }
                  size="base"
                  message={
                    cutoff.reason === 'enough'
                      ? t('merchant.cook.cutoffStartEnough', { count: waiting.length })
                      : cutoff.reason === 'waited'
                        ? t('merchant.cook.cutoffStartWaited', {
                            waited: formatDuration(oldestWaitSeconds, t),
                          })
                        : t('merchant.cook.cutoffHold', {
                            waited: formatDuration(cutoff.waitMoreSeconds ?? 0, t),
                            count: waiting.length,
                          })
                  }
                />
                {/* The load behind the advice, and only when it is worth knowing: a
                    kitchen at half capacity does not need to be told so. */}
                {policy.data && policy.data.load >= CROWDED_LOAD ? (
                  <Text className="text-warning text-xs">
                    {t('merchant.cook.cutoffBusy', {
                      percent: Math.round(policy.data.load * 100),
                    })}
                  </Text>
                ) : null}
              </Stack>
            ) : null}
            {rounds.map((round) => (
              <RoundCard
                key={round.readyBy}
                round={round}
                timezone={timezone}
                now={now}
                stacked={stacked}
                byId={byId}
                onMarkReady={startTick}
                marking={marking === round.readyBy}
                disabled={marking !== null || pending !== null}
              />
            ))}
          </Stack>
        )}
      </Stack>

      {/* The batch's own confirmation. It reads the round as it was drawn, so the list
          it names and the orders it will write cannot come apart while it is open — a
          late-accepted order can join an open window at any moment. */}
      <ConfirmDialog
        visible={pending !== null}
        title={t('merchant.cook.markRoundReadyTitle', { count: pending?.orderIds.length ?? 0 })}
        message={t('merchant.cook.markRoundReadyMessage')}
        confirmLabel={t('merchant.cook.markRoundReadyConfirm', {
          count: pending?.orderIds.length ?? 0,
        })}
        cancelLabel={t('common.cancel')}
        confirmVariant="success"
        busy={marking !== null}
        onConfirm={() => {
          if (pending) void runTick(pending);
        }}
        onCancel={() => setPending(null)}>
        {lateNote ? <Callout tone="warning" size="sm" message={lateNote} /> : null}
      </ConfirmDialog>
    </Screen>
  );
}
